/**
 * Переиспользуемые UI-компоненты без состояния.
 * - StatusPill — бейдж статуса соединения (подпись из ui-helpers).
 * - Link — клиентская навигация History-роутером поверх обычного `<a>`: сохраняет
 *   доступность (правый клик / открытие в новой вкладке / `Cmd+Click` работают),
 *   но не делает полную перезагрузку страницы.
 */
import { type AnchorHTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { SocketStatus } from '../ws';
import { navigate } from '../router';

export function StatusPill({ status }: { status: SocketStatus }): JSX.Element {
  const { t } = useTranslation();
  // 'idle' (ожидание соединения) маппится на ключ status.waiting — «ожидание».
  const key = status === 'idle' ? 'waiting' : status;
  return <span className={`pill pill--${status}`}>{t(`status.${key}`)}</span>;
}

export interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  className?: string;
  // children опциональны: при использовании Link внутри <Trans components={[...]}>
  // (react-i18next) дети инжектируются транспилятором перевода, а в статическом JSX
  // передаются явно.
  children?: ReactNode;
}

/** Клиентская ссылка: pushState-навигация без полной перезагрузки страницы.
 *  Rest-пропсы (data-testid, aria-*, id…) пробрасываются в <a> (Фаза 12, B4 —
 *  testid ссылки «Вся история» в кабинете); href/onClick каноничны после rest. */
export function Link({ to, className, children, ...rest }: LinkProps): JSX.Element {
  function handleClick(event: MouseEvent<HTMLAnchorElement>): void {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return; // пусть браузер обработает сам (новая вкладка и т.п.)
    }
    event.preventDefault();
    navigate(to);
  }

  return (
    <a {...rest} href={to} className={className} onClick={handleClick}>
      {children}
    </a>
  );
}
